import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { getCountries, getCountryCallingCode } from 'react-phone-number-input';
import en from 'react-phone-number-input/locale/en.json';
import {
  PhoneInputContainer,
  Label,
  Required,
  InputWrapper,
  CountrySelectWrapper,
  CountryDisplay,
  CountrySelect,
  PhoneNumberInput,
} from './StyledComponents';

// 🇺🇸 from 'US' — each letter maps to its regional indicator symbol
const flagEmoji = (iso) =>
  String.fromCodePoint(
    ...iso.split('').map((char) => 0x1f1a5 + char.charCodeAt(0))
  );

const COUNTRIES = getCountries()
  .map((iso) => ({
    iso,
    code: `+${getCountryCallingCode(iso)}`,
    flag: flagEmoji(iso),
    name: en[iso] || iso,
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

// Canonical country for calling codes shared across countries, so a bare
// '+1' style countryCode prop resolves to the expected flag
const SHARED_CODE_DEFAULTS = {
  '+1': 'US',
  '+7': 'RU',
  '+44': 'GB',
  '+61': 'AU',
};

const findCountry = (countryCode) => {
  if (!countryCode) return null;
  const byIso = COUNTRIES.find((c) => c.iso === countryCode);
  if (byIso) return byIso;
  const defaultIso = SHARED_CODE_DEFAULTS[countryCode];
  if (defaultIso) return COUNTRIES.find((c) => c.iso === defaultIso);
  return COUNTRIES.find((c) => c.code === countryCode) || null;
};

// Phone formatting helper
const formatPhoneNumber = (value, countryCode) => {
  const cleaned = value.replace(/\D/g, '');

  if (countryCode === '+1') {
    // US format: (555) 123-4567
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6)
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
      6,
      10
    )}`;
  }

  // Default format for other countries: space every 3-4 digits
  return cleaned.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
};

// Placeholder helper
const getPhonePlaceholder = (countryCode) => {
  switch (countryCode) {
    case '+1':
      return '(555) 123-4567';
    case '+44':
      return '7700 900000';
    case '+61':
      return '0412 345 678';
    case '+86':
      return '138 0000 0000';
    case '+33':
      return '06 12 34 56 78';
    case '+49':
      return '0151 23456789';
    case '+91':
      return '98765 43210';
    case '+81':
      return '090-1234-5678';
    case '+52':
      return '55 1234 5678';
    case '+234':
      return '0802 123 4567';
    case '+7':
      return '912 345-67-89';
    case '+82':
      return '010-1234-5678';
    case '+34':
      return '612 34 56 78';
    case '+971':
      return '050 123 4567';
    default:
      return 'Enter phone number';
  }
};

/**
 * PhoneInput - A reusable phone input component with country code selector
 * listing every country. The countryCode prop (and the first argument of
 * onCountryChange) is the calling code, e.g. '+1'; ISO country codes like
 * 'US' are also accepted. onCountryChange receives the ISO code as a second
 * argument for callers that need to distinguish countries sharing a calling
 * code.
 *
 * @param {string} label - Label text for the input
 * @param {string} value - The phone number value
 * @param {Function} onChange - Callback when phone number changes
 * @param {string} countryCode - Selected country ('+1' or 'US'); omit to let
 *   the component track the selection itself
 * @param {Function} onCountryChange - Callback (callingCode, isoCode)
 * @param {string} placeholder - Optional custom placeholder
 */
const PhoneInput = ({
  label = 'Phone Number',
  required = false,
  value,
  onChange,
  countryCode,
  onCountryChange,
  placeholder,
  ...props
}) => {
  const [internalIso, setInternalIso] = useState('US');

  const selectedCountry =
    findCountry(countryCode) ||
    findCountry(internalIso) ||
    COUNTRIES.find((c) => c.iso === 'US');

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value, selectedCountry.code);
    onChange(formatted);
  };

  const handleCountryChange = (e) => {
    const country = COUNTRIES.find((c) => c.iso === e.target.value);
    if (!country) return;
    setInternalIso(country.iso);
    if (onCountryChange) {
      onCountryChange(country.code, country.iso);
    }
  };

  return (
    <PhoneInputContainer>
      {label && (
        <Label>
          {label} {required && <Required>*</Required>}
        </Label>
      )}
      <InputWrapper>
        <CountrySelectWrapper>
          <CountryDisplay aria-hidden>{selectedCountry.flag}</CountryDisplay>
          <CountrySelect
            aria-label="Country"
            value={selectedCountry.iso}
            onChange={handleCountryChange}
          >
            {COUNTRIES.map((country) => (
              <option key={country.iso} value={country.iso}>
                {country.flag} {country.name} ({country.code})
              </option>
            ))}
          </CountrySelect>
        </CountrySelectWrapper>
        <PhoneNumberInput
          type="tel"
          placeholder={placeholder || getPhonePlaceholder(selectedCountry.code)}
          value={value}
          onChange={handlePhoneChange}
          {...props}
        />
      </InputWrapper>
    </PhoneInputContainer>
  );
};

PhoneInput.propTypes = {
  label: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  countryCode: PropTypes.string,
  onCountryChange: PropTypes.func,
  placeholder: PropTypes.string,
};

export default PhoneInput;
